import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { GrLanguage } from 'react-icons/gr'
import { SkeletonBox } from '@eduact/ed-components'
import {
	MainHeaderContainer,
	MobileTopBar,
	TopBarIconButton,
	TopBarActions,
	DesktopBar,
	DesktopLogoWrap,
	DesktopRHS,
	UserPrefsWrapper,
	HeaderCenter,
	LangPillButton,
	SubHeaderStrip,
	BackChip,
} from './Header.styled'
import { Button as AbwButton, Icon as AbwIcon } from '@Components/AbwaabPlus'
import CustomLink from '@Styles/CustomLink'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useAppDispatch } from '@Hooks/ReduxHooks'
import { logoutRequestAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useTheme } from '@eduact/student-theme'
import { useLayoutProvider } from '@Providers/LayoutProvider/LayoutProvider'
import { useRouteChrome } from '@Hooks/useRouteChrome'
import { useBack } from '@Hooks/useBack'
import WalletChip from './Components/Wallet/WalletChip'
import AppNav from '@Components/Layout/AppNav/AppNav'
import { setCurrentURL } from '@Redux/UtilsSlice/UtilsSlice'
import { defaultAssets } from '@Constants/assets.defaults'
import { excludedRedirections } from '@Providers/RoutingProvider/RoutingProvider'
import { Commands } from './Components/Commands/Commands'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDeviceInfo } from '@eduact/utils'

const hotKeysMap = {
	'Mac OS': { label: '⌘ + F', key: 'meta + f' },
	Windows: { label: 'Ctrl + F', key: 'ctrl + f' },
	Linux: { label: 'Ctrl + F', key: 'ctrl + f' },
}

const Header: React.FC = () => {
	const [fixed, setFixed] = useState(false)
	const headerRef = useRef<HTMLDivElement>(null)
	const { isAuth } = useAppSelector(state => state.auth)

	const { assets, assetsLoading } = useAppSelector(s => s.settings)

	const dispatch = useAppDispatch()
	const [t, i18n] = useTranslation('layouts', { keyPrefix: 'header' })
	const history = useNavigate()
	const location = useLocation()

	const { showWallet, embed, fixedHeader } = useLayoutProvider()
	const chrome = useRouteChrome()
	const showBack = !!chrome.showBack && !embed
	const pageTitle = chrome.titleKey ? t(chrome.titleKey) : undefined
	const goBack = useBack(chrome.backFallback ?? '/')
	const { app_wallet } = useAppSelector(s => s.settings)
	const { setTheme, theme } = useTheme()
	useEffect(() => {
		if (i18n.language === 'ar-EG') {
			setTheme?.({ ...theme, direction: 'rtl' })
		} else {
			setTheme?.({ ...theme, direction: 'ltr' })
		}
		window.addEventListener('scroll', onWindowScroll, false)
		return () => {
			window.removeEventListener('scroll', onWindowScroll, false)
		}
	}, [])

	// Publish the real header height so the (fixed-header) page content sits flush
	// beneath it at every breakpoint — avoids a gap/overlap when the header height
	// changes (auth state, per-route title row).
	useLayoutEffect(() => {
		const measure = () => {
			const h = headerRef.current?.offsetHeight
			if (h) document.documentElement.style.setProperty('--app-header-height', `${h}px`)
		}
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
	}, [isAuth, location.pathname])

	const { os } = useDeviceInfo({})

	const onWindowScroll = (e: Event) => {
		const scrollPosition = window.scrollY
		if (headerRef && headerRef.current != null) {
			if (scrollPosition > 300) {
				headerRef.current.style.zIndex = '99'
				setFixed(true)
			} else {
				headerRef.current.style.zIndex = ''
				setFixed(false)
			}
		}
	}
	const handleLogout = async () => {
		// AWAITED before the navigation below. `window.location.assign` cancels
		// in-flight requests, so dispatching this without awaiting would abort
		// POST /auth/logout and leave the session alive on the server while the
		// UI claimed otherwise — which is exactly the bug logout never having
		// existed server-side used to cause.
		await dispatch(logoutRequestAction())
		// Full-page load on logout guarantees a fresh store (an empty RTK Query
		// runningQueries map), so the landing data never gets stuck on skeletons after
		// the auth change. The reload also makes resetApiState() unnecessary.
		window.location.assign('/')
	}
	const changeLanguage = () => {
		i18n.changeLanguage(i18n.language === 'ar-EG' ? 'en-GB' : 'ar-EG')
		if (i18n.language === 'ar-EG') {
			setTheme?.({ ...theme, direction: 'rtl' })
		} else {
			setTheme?.({ ...theme, direction: 'ltr' })
		}
	}

	const handleAuthNavigation = (route: string) => {
		dispatch(setCurrentURL(location.pathname))
		history(route)
	}
	const { redirect: redirectCode } = useAppSelector(s => s.auth)

	const redirect = redirectCode && !excludedRedirections.includes(redirectCode)

	const [openCommands, setOpenCommands] = useState(false)
	useHotkeys(
		hotKeysMap[os.name as keyof typeof hotKeysMap]?.key || 'Ctrl + F',
		e => {
			setOpenCommands(!openCommands)
		},
		{
			enabled: true,
			preventDefault: true,
		},
		[openCommands]
	)

	const logo = (
		<>
			{assetsLoading && <SkeletonBox isLoading width="160px" height="42px" />}
			{!assetsLoading && <img alt="abwaab" src={assets.logo ?? defaultAssets.logo} />}
		</>
	)

	return (
		<MainHeaderContainer layoutConfigFixed={fixedHeader} fixed={fixed} ref={headerRef}>
			<Commands open={openCommands} onClose={setOpenCommands} />

			{/* ===== Mobile TopBar (<1024px) ===== */}
			{/* Guest (abwaab.com replica): logo + lang + country chip only — no auth
			    CTAs, like the site's mobile header. Authed (design-exact): logo +
			    wallet chip — the bottom AppNav carries navigation, and logout lives
			    in the حسابي links card only (a header logout was one accidental tap
			    away from ending the session; desktop keeps its header button). */}
			<MobileTopBar>
				<LogoLink to={embed ? '' : '/'} display="block" disabled={!!redirect}>
					{logo}
				</LogoLink>
				<TopBarActions>
					{!isAuth && (
						<>
							<TopBarIconButton type="button" onClick={changeLanguage} aria-label={t('language', { defaultValue: 'تغيير اللغة' })}>
								<GrLanguage />
							</TopBarIconButton>
							<CountryChipButton
								type="button"
								onClick={() => document.getElementById('footer-country-select')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
							>
								<img alt="" src="https://st.abwaab.com/public/icons/sequare_flags/egypt.png" />
								<span>{(i18n.language || '').toLowerCase().startsWith('ar') ? 'مصر' : 'Egypt'}</span>
							</CountryChipButton>
						</>
					)}
					{isAuth && app_wallet && showWallet && <WalletChip />}
				</TopBarActions>
			</MobileTopBar>

			{/* ===== Desktop bar (≥1024px) ===== */}
			{/* Guest (abwaab.com replica): auth CTAs on the inline-start edge, logo
			    CENTERED, lang pill + country chip on the inline-end edge. The chip
			    is not a dropdown — like the site it scrolls to the footer's country
			    selector. Search stays reachable via Ctrl+F only.
			    Authed (design + kept utilities as icons): logo · nav row · search
			    icon · lang icon · wallet chip · red logout. */}
			<DesktopBar>
				{!isAuth ? (
					<>
						<GuestEdge>
							<AbwButton variant="primary" onClick={() => handleAuthNavigation('/signup')}>
								{t('start_with_abwaab', { defaultValue: 'ابدأ مع أبواب' })}
							</AbwButton>
							<AbwButton variant="outline" onClick={() => handleAuthNavigation('/login')}>
								{t('have_account', { defaultValue: 'عندي حساب' })}
							</AbwButton>
						</GuestEdge>
						<DesktopLogoWrap>
							<LogoLink to={embed ? '' : '/'} display="block" disabled={!!redirect}>
								{logo}
							</LogoLink>
						</DesktopLogoWrap>
						<GuestEdge $end>
							<LangPillButton onClick={changeLanguage}>🌐 {i18n.language === 'ar-EG' ? 'English' : 'عربي'}</LangPillButton>
							<CountryChipButton
								type="button"
								onClick={() => document.getElementById('footer-country-select')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
							>
								<img alt="" src="https://st.abwaab.com/public/icons/sequare_flags/egypt.png" />
								<span>{(i18n.language || '').toLowerCase().startsWith('ar') ? 'مصر' : 'Egypt'}</span>
							</CountryChipButton>
						</GuestEdge>
					</>
				) : (
					<>
						<DesktopLogoWrap>
							<LogoLink to={embed ? '' : '/'} display="block" disabled={!!redirect}>
								{logo}
							</LogoLink>
						</DesktopLogoWrap>
						{!embed ? <AppNav variant="header" /> : <HeaderCenter />}
						<DesktopRHS>
							<UserPrefsWrapper>
								<TopBarIconButton type="button" onClick={() => !redirect && setOpenCommands(true)} aria-label={t('command_bar')}>
									<AbwIcon name="search" size={20} />
								</TopBarIconButton>
								<TopBarIconButton type="button" onClick={changeLanguage} aria-label={t('language', { defaultValue: 'تغيير اللغة' })}>
									<GrLanguage />
								</TopBarIconButton>
								{app_wallet && showWallet && <WalletChip />}
								{!embed && (
									<RedLogoutButton type="button" onClick={() => handleLogout()} aria-label={t('logout')}>
										<span className="mirror">
											<AbwIcon name="log-out" size={20} />
										</span>
									</RedLogoutButton>
								)}
							</UserPrefsWrapper>
						</DesktopRHS>
					</>
				)}
			</DesktopBar>

			{/* ===== Back strip (all breakpoints) — drill-down routes only ===== */}
			{showBack && (
				<SubHeaderStrip>
					<BackChip type="button" onClick={goBack} aria-label={t('back')}>
						<span className="mirror">
							<AbwIcon name="arrow-left" size={16} />
						</span>
						{t('back')}
					</BackChip>
					{pageTitle && <span className="title">{pageTitle}</span>}
				</SubHeaderStrip>
			)}
		</MainHeaderContainer>
	)
}

export default Header

/* Design's logout affordance: bare 40×40 icon button in the error color; the
   exit icon mirrors in RTL so the door always opens outward. */
const RedLogoutButton = styled.button`
	width: 40px;
	height: 40px;
	border: none;
	background: transparent;
	color: var(--error);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	.mirror {
		display: flex;
		html[dir='rtl'] & {
			transform: scaleX(-1);
		}
	}
`

/* Guest desktop edges: equal-flex zones keeping the logo dead-centre.
   First zone hugs inline-start (CTAs), $end hugs inline-end (lang + country). */
const GuestEdge = styled.div<{ $end?: boolean }>`
	flex: 1;
	display: flex;
	align-items: center;
	gap: 12px;
	justify-content: ${p => (p.$end ? 'flex-end' : 'flex-start')};
`

/* Static country chip (flag + مصر) — scrolls to the footer's selector on click. */
const CountryChipButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	height: 36px;
	padding: 0 12px;
	border: 1px solid var(--border);
	border-radius: var(--r-md);
	background: var(--surface-card);
	color: var(--text-secondary);
	font: var(--fw-medium) 14px var(--font-sans);
	cursor: pointer;
	transition: border-color var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
	&:hover {
		border-color: var(--blue-20);
		color: var(--blue);
	}
	img {
		width: 20px;
		height: 20px;
		object-fit: contain;
	}
`

const LogoLink = styled(CustomLink)<{ disabled?: boolean }>`
	align-items: center;
	display: flex;
	height: 42px;
	max-width: 160px;
	img {
		max-height: 100%;
		max-width: 100%;
		object-fit: contain;
	}
	pointer-events: ${p => p.disabled && 'none'};
`
